import React, { useState, useEffect, useRef } from "react";
import { Button } from "reactstrap";
import "react-datepicker/dist/react-datepicker.css";
import SnowFlakeChart from "./SnowFlakeChart";
import SnowFlakeStats from "./SnowFlakeStats";
import SnowFlakeAttribution from "./SnowFlakeAttribution";
import SnowFlakeInsights from "./SnowFlakeInsights";
import { apiClient } from "../../apiClient";
import { endpoints } from "../../configs";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSync } from "@fortawesome/free-solid-svg-icons";
import { ActivityIcon } from "../../assets/img/icons";

import "./SnowFlakeDashboard.scss"; // We might keep a very minimal CSS for scrollbars only
import Form from "../../components/base/Form";
import CustomDateFilter from "../../components/base/CustomDateFilter";
import Toast from "../../components/base/Toast";
import { getKeyValueByObject } from "../../lib/helper";
import { SETTINGS_ENABLE_SNOWFLAKE } from "../../setting/Constants";
import Page404 from "../Page404";
import BooleanUtil from "../../helpers/Boolean";

const SnowFlakeDashboard = props => {
  const { settings } = props;
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [spikesData, setSpikesData] = useState([]);
  const [attributionData, setAttributionData] = useState([]);
  const [correlateData, setCorrelateData] = useState([]);
  const [corrStartDate, setCorrStartDate] = useState(
    moment().format("YYYY-MM-DD")
  );
  const [corrEndDate, setCorrEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const enableSnowflake = settings
    ? getKeyValueByObject(settings, SETTINGS_ENABLE_SNOWFLAKE)
    : "";

  const fetchTableData = async (page = currentPage) => {
    setTableLoading(true);
    try {
      const start = moment(corrStartDate)
        .startOf("day")
        .toISOString();
      const end = moment(corrEndDate)
        .endOf("day")
        .toISOString();
      const [attrRes, corrRes] = await Promise.all([
        apiClient.get(
          `${endpoints().snowFlakeAPI}/attribution?start=${start}&end=${end}`
        ),
        apiClient.get(
          `${
            endpoints().snowFlakeAPI
          }/correlate?start=${start}&end=${end}&page=${page}&pageSize=${pageSize}`
        )
      ]);
      setAttributionData((attrRes.data && attrRes.data.data) || []);
      setCorrelateData((corrRes.data && corrRes.data.correlations) || []);
      setTotalRecords((corrRes.data && corrRes.data.totalCount) || 0);
    } catch (err) {
      Toast.error(err.response ? err.response.data.message : err.message);
    } finally {
      setTableLoading(false);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    setTableLoading(true);
    try {
      const start = moment(corrStartDate)
        .startOf("day")
        .toISOString();
      const end = moment(corrEndDate)
        .endOf("day")
        .toISOString();
      const [spikesRes, attrRes, corrRes] = await Promise.all([
        apiClient.get(
          `${endpoints().snowFlakeAPI}/spikes?start=${start}&end=${end}`
        ),
        apiClient.get(
          `${endpoints().snowFlakeAPI}/attribution?start=${start}&end=${end}`
        ),
        apiClient.get(
          `${
            endpoints().snowFlakeAPI
          }/correlate?start=${start}&end=${end}&page=1&pageSize=${pageSize}`
        )
      ]);
      setSpikesData((spikesRes.data && spikesRes.data.data) || []);
      setAttributionData((attrRes.data && attrRes.data.data) || []);
      setCorrelateData((corrRes.data && corrRes.data.correlations) || []);
      setTotalRecords((corrRes.data && corrRes.data.totalCount) || 0);
    } catch (err) {
      Toast.error(err.response ? err.response.data.message : err.message);
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    fetchAll();
  }, [corrStartDate, corrEndDate, pageSize]);

  useEffect(() => {
    if (currentPage > 1 || attributionData.length > 0) {
      fetchTableData(currentPage);
    }
  }, [currentPage]);

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const totalPages = Math.ceil(totalRecords / pageSize);

  // ─── KPI Calculations ───────────────────────────────────────────────────────
  const totalCredits = attributionData.reduce(
    (sum, r) => sum + parseFloat(r.TOTAL_CREDITS || 0),
    0
  );
  const totalConsumers = attributionData.length;
  const avgUsageDays =
    attributionData.length > 0
      ? (
          attributionData.reduce(
            (sum, r) => sum + parseInt(r.USAGE_DAYS || 0),
            0
          ) / attributionData.length
        ).toFixed(1)
      : 0;
  const matchRate =
    correlateData.length > 0
      ? (
          (correlateData.filter(
            c => c.matched_spikes && c.matched_spikes.length > 0
          ).length /
            correlateData.length) *
          100
        ).toFixed(1)
      : 0;

  // ─── Trend Calculation ──────────────────────────────────────────────────
  const calculateTrend = () => {
    if (!spikesData || spikesData.length === 0) return { value: 0, isUp: true };

    const sorted = [...spikesData].sort(
      (a, b) => new Date(a.USAGE_DATE) - new Date(b.USAGE_DATE)
    );
    if (sorted.length < 2) return { value: 0, isUp: true };

    const firstDate = moment(sorted[0].USAGE_DATE);
    const lastDate = moment(sorted[sorted.length - 1].USAGE_DATE);
    const duration = lastDate.diff(firstDate, "days");
    const midPoint = firstDate.clone().add(duration / 2, "days");

    const recent = sorted
      .filter(s => moment(s.USAGE_DATE).isAfter(midPoint))
      .reduce((sum, s) => sum + parseFloat(s.CREDITS_USED || 0), 0);
    const older = sorted
      .filter(s => moment(s.USAGE_DATE).isSameOrBefore(midPoint))
      .reduce((sum, s) => sum + parseFloat(s.CREDITS_USED || 0), 0);

    if (older === 0) return { value: recent > 0 ? 100 : 0, isUp: true };
    const diff = ((recent - older) / older) * 100;
    return { value: Math.abs(diff).toFixed(1), isUp: diff >= 0 };
  };

  const trend = calculateTrend();

  if (BooleanUtil.isFalse(enableSnowflake)) {
    return <Page404 />;
  }

  return (
    <div className="snowflake-dashboard animated fadeIn">
      {/* Header Section */}
      <div className="dashboard-header d-flex justify-content-between align-items-center mb-4">
        <div>
          <div className="d-flex align-items-center">
            <div className="snowflake-icon-container mr-3">
              <ActivityIcon />
            </div>
            <div>
              <h2 className="title mb-0">Snowflake Insight</h2>
              <p className="text-muted mb-0">
                Real-time usage attribution & activity correlation
              </p>
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center">
          <Button
            color="primary"
            className="btn-refresh d-flex align-items-center p-2"
            onClick={fetchAll}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faSync} className="m-2" spin={loading} />
          </Button>
        </div>
      </div>
      <div className="mb-2">
        <Form
          initialValues={{
            start_date: corrStartDate,
            end_date: corrEndDate
          }}
          className="w-100"
          width="100%"
          enableReinitialize
        >
          <CustomDateFilter
            initialStartDate={corrStartDate}
            initialEndDate={corrEndDate}
            defaultFilter="today"
            onRangeChange={({ start, end }) => {
              setCorrStartDate(start);
              setCorrEndDate(end);
            }}
          />
        </Form>
      </div>
      <SnowFlakeStats
        totalCredits={totalCredits}
        totalConsumers={totalConsumers}
        avgUsageDays={avgUsageDays}
        matchRate={matchRate}
        trend={trend}
      />

      <SnowFlakeChart
        spikesData={spikesData}
        attributionData={attributionData}
        correlateData={correlateData}
        trend={trend}
        matchRate={matchRate}
        loading={loading}
      />

      <SnowFlakeAttribution
        attributionData={attributionData}
        correlateData={correlateData}
        corrStartDate={corrStartDate}
        corrEndDate={corrEndDate}
        currentPage={currentPage}
        pageSize={pageSize}
        totalRecords={totalRecords}
        totalPages={totalPages}
        handlePageChange={handlePageChange}
        setPageSize={setPageSize}
        setCurrentPage={setCurrentPage}
        loading={tableLoading}
      />

      <div className="mt-5">
        <SnowFlakeInsights startDate={corrStartDate} endDate={corrEndDate} />
      </div>
    </div>
  );
};

export default SnowFlakeDashboard;
